import { cache } from 'react';
import { supabase } from './supabase';
import { Vehicle } from '../utils/types';
import { DEALERSHIP_PUBLIC_COLS } from './website-config-cols';

export const getVehiclesByClientId = async (clientId: string) => {
  try {
    const { data, error } = await supabase
      .from('vehicles')
      .select(
        `
        id,created_at,updated_at,client_id,year,price,discount_percentage,mileage,main_image,gallery,video_url,features,transmission,description,brand_id,model_id,dealership_id,fuel_type_id,category_id,color_id,condition_id,status_id,is_consigned,seller_id,state_updated_at,label,traction,version_name,label_color,iva_exento,is_historical_import,
        brand:brand_id(id, name),
        model:model_id(id, name)
      `
      )
      .eq('client_id', clientId);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    throw error;
  }
};

// Cacheado por request (React cache): generateMetadata, los JSON-LD y el body
// comparten un único fetch del vehículo. OJO cache() memoiza por argumentos:
// todos los callers de un mismo request deben pasar el mismo clientId.
export const getVehicleById = cache(
  async (vehicleId: string, clientId?: string | number) => {
    try {
      let query = supabase
        .from('vehicles')
        .select(
          `
        id,created_at,updated_at,client_id,year,price,discount_percentage,mileage,main_image,gallery,video_url,features,transmission,description,brand_id,model_id,dealership_id,fuel_type_id,category_id,color_id,condition_id,status_id,is_consigned,seller_id,state_updated_at,label,traction,version_name,label_color,iva_exento,is_historical_import,
        brand:brand_id(id, name),
        model:model_id(id, name),
        fuel_type:fuel_type_id(id, name),
        color:color_id(id, name),
        condition:condition_id(id, name),
        category:category_id(id, name),
        dealership:dealership_id(${DEALERSHIP_PUBLIC_COLS}),
        status:status_id(name, show_in_web),
        seller_id
      `
        )
        .eq('id', vehicleId);
      // Hardening cross-tenant: con clientId presente, una ficha ajena al host
      // no se sirve (antes cualquier id renderizaba bajo cualquier dominio).
      if (clientId != null) {
        query = query.eq('client_id', clientId);
      }
      const { data, error } = await query.single();
      // PGRST116 = cero filas: el vehículo no existe (o es de otro tenant).
      // Se distingue del error transitorio para que el caller pueda 404ear
      // sin riesgo de 404ear fichas válidas por un hipo de red.
      if (error) {
        if ((error as { code?: string }).code === 'PGRST116') return null;
        throw error;
      }
      // Un vehículo que solo existe porque una importación reconstruyó la
      // historia de la automotora no tiene ficha pública: nunca estuvo a la
      // venta en este sitio. Se trata igual que 'no existe' (404 + noindex).
      // Solo aplica al vehículo creado por la importación: un auto real y
      // viejo conocido al que además se le adjuntó su venta histórica
      // conserva su ficha, porque ya era pública antes de importar.
      if ((data as { is_historical_import?: boolean | null })?.is_historical_import === true) {
        return null;
      }
      return data as unknown as Vehicle;
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      throw error;
    }
  }
);

// Vehículos visibles del tenant (para enlaces internos indexables en el home).
// Filtra por show_in_web y excluye Vendido/Reservado, igual que el sitemap.
export const getVisibleVehiclesByClientId = cache(
  async (clientId: string | number) => {
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select(
          `id, year, price, is_historical_import, brand:brand_id(name), model:model_id(name), status:status_id(name, show_in_web)`
        )
        .eq('client_id', clientId);
      if (error) throw error;
      return (data || []).filter((v: any) => {
        const st = Array.isArray(v.status) ? v.status[0] : v.status;
        if (!st) return false;
        const show =
          typeof st.show_in_web === 'boolean' ? st.show_in_web : st.name === 'Publicado';
        if (v.is_historical_import === true) return false;
        return show && st.name !== 'Vendido' && st.name !== 'Reservado';
      });
    } catch (error) {
      console.error('Error fetching visible vehicles:', error);
      return [];
    }
  }
);

// Similares para la ficha (sección server-rendered): mismo tenant, visibles,
// rankeados marca > categoría > resto con desempate por precio más cercano.
export const getSimilarVehicles = cache(
  async (
    clientId: string | number,
    current: { id: string | number; brand_id?: number | null; category_id?: number | null; price?: number | null },
    limit = 3
  ) => {
    try {
      // Select masivo SIN main_image (URLs largas × inventarios de 300-500
      // autos); las imágenes de los elegidos se traen aparte con .in().
      const { data, error } = await supabase
        .from('vehicles')
        .select(
          `id, year, price, brand_id, category_id, is_historical_import,
           brand:brand_id(name), model:model_id(name), status:status_id(name, show_in_web)`
        )
        .eq('client_id', clientId)
        .neq('id', current.id);
      if (error) throw error;

      const visible = (data || []).filter((v: any) => {
        if (String(v.id) === String(current.id)) return false;
        const st = Array.isArray(v.status) ? v.status[0] : v.status;
        if (!st) return false;
        const show =
          typeof st.show_in_web === 'boolean' ? st.show_in_web : st.name === 'Publicado';
        if (v.is_historical_import === true) return false;
        return show && st.name !== 'Vendido' && st.name !== 'Reservado';
      });

      const rank = (v: any) => {
        if (current.brand_id != null && v.brand_id === current.brand_id) return 0;
        if (current.category_id != null && v.category_id === current.category_id) return 1;
        return 2;
      };
      const priceDelta = (v: any) =>
        current.price != null && v.price != null
          ? Math.abs(v.price - current.price)
          : Number.MAX_SAFE_INTEGER;

      const picked = visible
        .sort((a: any, b: any) => rank(a) - rank(b) || priceDelta(a) - priceDelta(b))
        .slice(0, limit);

      if (picked.length) {
        const { data: images } = await supabase
          .from('vehicles')
          .select('id, main_image')
          .in('id', picked.map((v: any) => v.id));
        const byId = new Map((images || []).map((i: any) => [String(i.id), i.main_image]));
        picked.forEach((v: any) => {
          v.main_image = byId.get(String(v.id)) ?? null;
        });
      }
      return picked;
    } catch (error) {
      // La sección de similares nunca debe tumbar la ficha.
      console.error('Error fetching similar vehicles:', error);
      return [];
    }
  }
);

// Sucursales del tenant, para el schema AutoDealer (NAP + geo + horarios).
export const getDealershipsByClientId = cache(async (clientId: string | number) => {
  try {
    // Las columnas públicas de siempre: el JSON-LD arma NAP + geo con varias de ellas.
    // El horario YA NO sale de acá (ticket #59): va por
    // `fn_dealership_public_hours`, que lee la fuente única de Agenda.
    const { data, error } = await supabase
      .from('dealerships')
      .select<string, Record<string, any>>(DEALERSHIP_PUBLIC_COLS)
      // Sin ORDER BY, "la principal" es la que devuelva Postgres, y eso cambia
      // con cualquier UPDATE de la fila. Daba igual mientras el JSON-LD no
      // dijera nada de la sucursal; ahora de acá sale la comuna que se publica.
      .eq('client_id', clientId)
      .order('id');
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching dealerships:', error);
    return [];
  }
});

export const incrementVehicleViews = async (vehicleId: number) => {
  try {
    const { data, error } = await supabase.rpc('increment_vehicle_views', {
      vehicle_id: vehicleId,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error incrementing vehicle views:', error);
    throw error;
  }
};

interface SearchParams {
  brand?: string;
  model?: string;
  year?: {
    min?: number;
    max?: number;
  };
  price?: {
    min?: number;
    max?: number;
  };
  mileage?: {
    min?: number;
    max?: number;
  };
  transmission?: string;
  fuel_type?: string;
  condition?: string;
  color?: string;
}

export const searchVehicles = async (params: SearchParams) => {
  try {
    console.log('Searching vehicles with params:', params);
    let query = supabase.from('vehicles').select(
      `
        id,created_at,updated_at,client_id,year,price,discount_percentage,mileage,main_image,gallery,video_url,features,transmission,description,brand_id,model_id,dealership_id,fuel_type_id,category_id,color_id,condition_id,status_id,is_consigned,seller_id,state_updated_at,label,traction,version_name,label_color,iva_exento,is_historical_import,
        brand:brand_id(*),
        model:model_id(*)
      `,
      { count: 'exact' }
    );

    // La historia reconstruida no se busca. Va como filtro de la consulta y
    // no en memoria porque esta funcion pagina en la base (count exacto): un
    // filtro posterior devolveria paginas cortas y un total inflado.
    // `not(is, true)` y no `neq`: deja pasar false y tambien NULL, por si
    // alguna consulta futura llega desde una vista sin la columna.
    query = query.not('is_historical_import', 'is', true);

    // Apply filters based on the search parameters
    if (params.model) {
      console.log('Model:', params.model);
      const { data: modelData } = await supabase
        .from('models')
        .select('id')
        .ilike('name', params.model)
        .maybeSingle();

      if (modelData) {
        query = query.eq('model_id', modelData.id);
      }
    } else if (params.brand) {
      console.log('Brand:', params.brand);
      const { data: brandData } = await supabase
        .from('brands')
        .select('id')
        .ilike('name', params.brand)
        .maybeSingle();

      if (brandData) {
        query = query.eq('brand_id', brandData.id);
      }
    }

    if (params.year?.min) {
      query = query.gte('year', params.year.min);
    }

    if (params.year?.max) {
      query = query.lte('year', params.year.max);
    }

    if (params.price?.min) {
      query = query.gte('price', params.price.min);
    }

    if (params.price?.max) {
      query = query.lte('price', params.price.max);
    }

    if (params.mileage?.min) {
      query = query.gte('mileage', params.mileage.min);
    }

    if (params.mileage?.max) {
      query = query.lte('mileage', params.mileage.max);
    }

    if (params.transmission) {
      query = query.ilike('transmission', params.transmission);
    }

    if (params.fuel_type) {
      query = query.ilike('fuel_type', params.fuel_type);
    }

    if (params.condition) {
      query = query.ilike('condition', params.condition);
    }

    if (params.color) {
      query = query.ilike('color', params.color);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }
    console.log('Data:', data);

    return { vehicles: data || [], count: count || 0 };
  } catch (error) {
    console.error('Error searching vehicles:', error);
    return { vehicles: [], count: 0 };
  }
};
